import Promise from 'bluebird';
import _ from 'lodash';

import worldMap from '../../core/map';
import { pinBuilder, pinSizes } from '../../core/map/pinBuilder';
import streetView from '../../core/map/streetview';
import templates from '../../core/templates';
import ColorBrewer from '../../core/tools/colorbrewer';
import environment from '../../environment';
import HaloLayer from './../HaloLayer';
import Leaflet from './../Leaflet';

// import 'mapbox.js';
const LayerControls = name => require('../../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./../index').default.get(name, currentMap);
};

class StoresAfterSalesLayer {
    constructor(contextType, currentMap) {
        let context;
        let _total = 0;
        const mapId = +currentMap._id;
        const clusterImage = new Image();

        switch (contextType) {
            case 'NetworkAfterSales': {
                context = 'NetworkAfterSales';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_toll.png`;
                break;
            }
            case 'CompetitorsAfterSales': {
                context = 'CompetitorsAfterSales';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_stores.png`;
                break;
            }
            case 'StoreInactiveAfterSales': {
                context = 'StoreInactiveAfterSales';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_stores.png`;
            }
        }

        const onComplete = features => {
            LayerControls(contextType).show({ refresh: true, show: true }, features);
        };

        const onRemove = () => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) {
                    LayerControls('Voronoi').update(mapId);
                }
            } catch (error) {
                console.log('Layer not avaliable');
            }
        };

        const onReload = features => {
            const f = LayerControls(contextType);
            const layerType = f.getControls(mapId).buttonGroup.layerType.type;

            if (layerType === 'decluster') {
                get(contextType, currentMap).setOptions({ controlRange: 0.025, type: 'cluster' });
            } else if (layerType === 'cluster' && get(contextType, currentMap).options.controlRange === 1) {
                get(contextType, currentMap).setOptions({ controlRange: 30 });
            }

            return new Promise(resolve => {
                resolve(features);
            });
        };

        const onClick = (feature, layer) => {
            LayerControls(contextType).highlight(layer);
            LayerControls(contextType).show({
                show: true,
                id: feature.properties.Id
            });

            layer.closePopup();
            return new HaloLayer(currentMap, layer, feature, contextType, context);
        };

        const createClusterTemplate = cluster => {
            const f = LayerControls(contextType);
            const layerType = f.getControls(mapId).buttonGroup.layerType.type;

            const getImage = (properties) => `${environment.get('IMAGES_HOST')}/aftersales/pin_${StoresAfterSalesLayer.getPinBaseName(contextType, properties)}_xs.png`;

            const images = _(cluster.getAllChildMarkers())
                .map(m => getImage(m.feature.properties))
                .uniqBy(i => i)
                .value();

            const data = {
                value: cluster.getChildCount(),
                images
            };

            if (currentMap.getZoom() < 14 && layerType !== 'decluster') {
                return pinBuilder.create.pin.imageWithValue(data, clusterImage);
            } else {
                return pinBuilder.create.pin.cluster(data);
            }
        };

        const createPopupTemplate = (properties, layer) =>
            new Promise((resolve) => {
                const hasServiceType = environment.get('CONFIG.FILTERS.SERVICE_TYPES').some(serviceType => properties.ServiceTypeId.includes(serviceType.id));
                properties.ServiceType = hasServiceType ? properties.ServiceTypeName : '';

                const $template = $(templates('map-layer-stores-popup-after-sales', properties));

                const coordinates = {
                    lat: layer.feature.geometry.coordinates[1],
                    lng: layer.feature.geometry.coordinates[0]
                };

                streetView.thumbnail($template.find('.leaflet-popup-image'), coordinates).then(() => {
                    resolve($template);
                });
            });

        let urlApi = '';
        switch (contextType) {
            case 'NetworkAfterSales':
                urlApi = `${environment.get('API_HOST')}/aftersales/stores?type=1`;
                break;
            case 'CompetitorsAfterSales':
                urlApi = `${environment.get('API_HOST')}/aftersales/stores?type=2`;
                break;
            case 'StoreInactiveAfterSales':
                urlApi = `${environment.get('API_HOST')}/aftersales/stores/getstoresinactives`;
                break;
        }

        return new Leaflet.GeoJSONLayer({
            map: currentMap,
            id: `spatial_${contextType.toLowerCase()}_layer`,
            remote: {
                url: urlApi,
                method: 'GET',
            },
            scaleRange: [5, 20],
            filter: {
                location: true
            },
            showAll: true,
            autofocus: false,
            uniqueField: 'Id',
            cache: true,
            sort: 'Nome',
            click: onClick,
            done: onComplete,
            remove: onRemove,
            reload: onReload,
            popup: {
                template: createPopupTemplate,
                trigger: 'mouseover',
                options: {
                    offset: L.point(0, -50),
                    closeButton: false,
                    autoclose: true,
                    maxWidth: 600,
                },
                single: true
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    disableAtZoom: 20,
                    template: createClusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_GREEN)
                },
                style: {
                    icon: {
                        iconUrl: 'asdfasdfasdf',
                        getUrl: StoresAfterSalesLayer.getApplicatorUrlIcon(contextType),
                        iconSize: StoresAfterSalesLayer.getIconSize(contextType),
                        iconAnchor: [25, 60],
                        labelAnchor: [25, -32],
                        className: 'store-marker-icon',
                    },
                    riseOnHover: true
                }
            }
        });
    }

    static getIconSize = () =>
        (data) => {
            const { IsActive } = data;

            if (IsActive)
                return [38, 48];


            return [50, 64];
        }

    /**
     * Separador que o backend usa ao compor PinName do pin especial: "{rede} ${nome do pin}".
     */
    static SPECIAL_PIN_SEPARATOR = ' $';

    static getPinBaseName = (contextType, properties) => {
        const pinName = String(properties.PinName);
        const isSpecialPin = pinName.indexOf(StoresAfterSalesLayer.SPECIAL_PIN_SEPARATOR) > -1;

        if (contextType !== 'NetworkAfterSales' || !isSpecialPin) {
            return pinName.toUnderscoreCase();
        }

        const [clientBrand] = environment.get('CONFIG.FILTERS.CLIENT_BRANDS') || [];

        if (!clientBrand) {
            return pinName.toUnderscoreCase();
        }

        const servicePinName = pinName.split(StoresAfterSalesLayer.SPECIAL_PIN_SEPARATOR).pop();

        return `${clientBrand.nome} ${servicePinName}`.toUnderscoreCase();
    }

    static getApplicatorUrlIcon = contextType => {
        return (data) => {
            const { imgSuffix } = get(contextType).options.symbology.style.icon;
            const { IsPinTire } = data;
            const url = `${environment.get('IMAGES_HOST')}/aftersales`;

            let pin = StoresAfterSalesLayer.getPinBaseName(contextType, data);

            if (IsPinTire) {
                pin = 'pneus';
            }

            if (contextType === "StoreInactiveAfterSales") {
                pin = `${pin}_inativa`;
            }

            return `${url}/pin_${pin}${(imgSuffix || '')}.png`;
        };
    }
}

export default StoresAfterSalesLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/AfterSales/StoresAfterSalesLayer.js